import type { HttpResponseType, SourceConfig } from 'bitmovin-player'
import type { PreviewDrm } from '../types/vod'

// Mirrors apps/tc-web/src/components/Player/drmSourceConfig.ts. Widevine is
// preferred so Chromium never negotiates PlayReady, which mis-decrypts the cbcs
// streams Amagi packages. EZDRM takes the token as CustomData on every license
// URL; FairPlay needs the skd:// content id, Uint16 init data, a raw SPC body
// and the CKC handed back as bytes.
export function buildDrmSourceConfig(
  drm: PreviewDrm,
  fairplayLicenseResponseType: HttpResponseType,
): NonNullable<SourceConfig['drm']> {
  const config: NonNullable<SourceConfig['drm']> = {
    preferredKeySystems: ['widevine', 'playready', 'fairplay'],
  }

  for (const license of drm.licenses) {
    const LA_URL = withCustomData(license.url, drm.token)
    switch (license.system) {
      case 'WIDEVINE':
        config.widevine = { LA_URL }
        break
      case 'PLAYREADY':
        config.playready = { LA_URL }
        break
      case 'FAIRPLAY':
        config.fairplay = {
          LA_URL,
          ...(license.cert_url ? { certificateURL: license.cert_url } : {}),
          prepareContentId: (contentId) => {
            const scheme = 'skd://'
            const at = contentId.toLowerCase().indexOf(scheme)
            return at < 0 ? contentId : contentId.slice(at + scheme.length)
          },
          prepareMessage: (event) =>
            new Blob([event.message], { type: 'application/octet-binary' }),
          headers: { 'content-type': 'application/octet-stream' },
          useUint16InitData: true,
          licenseResponseType: fairplayLicenseResponseType,
          prepareLicenseAsync: async (license: Blob) =>
            new Uint8Array(await license.arrayBuffer()),
        }
        break
    }
  }

  return config
}

/** Writes the token onto a license URL as EZDRM's CustomData, replacing any already there. */
export function withCustomData(url: string, token: string): string {
  const queryAt = url.indexOf('?')
  const base = queryAt < 0 ? url : url.slice(0, queryAt)
  const existing = queryAt < 0 ? [] : url.slice(queryAt + 1).split('&')
  const kept = existing.filter((param) => param !== '' && !/^CustomData=/i.test(param))
  kept.push(`CustomData=${encodeURIComponent(token)}`)
  return `${base}?${kept.join('&')}`
}
